import { Injectable } from "@angular/core";
import { AccountAppCommand } from "@fabric-types/app-account";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { DocumentRef, groupSwitchMap, PlatformService, TelemetryService } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { asyncScheduler, merge, of } from "rxjs";
import { catchError, filter, map, observeOn, pairwise, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { AppError } from "../app-error/app-error.model";
import { AUTH_ACTION_TYPE, AuthAction } from "../auth/auth.action";
import { AUTH_MESSAGE_CODES } from "../auth/auth.const";
import { SessionEndReason } from "../auth/auth.model";
import { SESSION_ACTION_TYPE, SessionAction } from "../auth/session.action";
import { SessionSelector } from "../auth/session.selector";
import { MessageScopeType, MessageType } from "../message/message.model";
import { MessageService } from "../message/message.service";
import { CoreState } from "../state";

import { CUSTOMER_MANAGEMENT_ACTION_TYPE, CustomerManagementAction } from "./customer-management.action";
import {
	CUSTOMER_MANAGEMENT_MESSAGE_CODES,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	PRIVACY_POLICY_AUTH_CODES,
	TERMS_AND_CONDITIONS_AUTH_CODES,
} from "./customer-management.const";
import {
	ConfirmCogaCustomerProfileRequest,
	ConfirmPegaCustomerProfileRequest,
	Customer,
	CustomerDetailSection,
	CustomerManagementService,
	EmailAvailability,
	KycState,
} from "./customer-management.model";
import { CustomerManagementSelector } from "./customer-management.selector";

@Injectable()
export class CustomerManagementEffect {
	getCustomer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomer, AUTH_ACTION_TYPE.sessionStart),
			observeOn(asyncScheduler), // jurisdiction should run first
			switchMap(() =>
				this.service.getCustomer().pipe(
					map((response) => this.action.getCustomerSuccess(response)),
					catchError((response: AppError) => of(this.action.getCustomerFail(response)))
				)
			)
		)
	);

	getCustomerDetail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetail),
			map((x: ActionWithPayload<CustomerDetailSection>) => x.payload),
			groupSwitchMap((section: CustomerDetailSection) =>
				this.service.getCustomerDetail(section).pipe(
					map((response) => this.action.getCustomerDetailSuccess(response)),
					catchError((response: AppError) => of(this.action.getCustomerDetailFail(response)))
				)
			)
		)
	);

	getCustomerSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerSuccess),
				map((x: ActionWithPayload<Partial<Customer>>) => x.payload),
				pairwise(),
				filter(
					([prev, curr]) =>
						prev.customerId === curr.customerId &&
						prev.kyc?.state === KycState.Requested &&
						curr.kyc?.state === KycState.Accepted
				),
				tap(([, customer]) => {
					this.telemetryService.track({
						event: GtmEventAction.AccountVerificationConfirmed,
						RegistrationType: customer.jurisdiction,
						GUID: customer.customerId,
					});
				})
			),
		{ dispatch: false }
	);

	updateCustomer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomer),
			map((x: ActionWithPayload<Partial<Customer>>) => x.payload),
			switchMap((customer) =>
				this.service.updateCustomer(customer).pipe(
					map((response) => this.action.updateCustomerSuccess(response)),
					catchError((response: AppError) => of(this.action.updateCustomerFail(response)))
				)
			)
		)
	);

	verifyToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyToken),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.verifyToken(token).pipe(
					map(() => this.action.verifyTokenSuccess()),
					catchError((response: AppError) => of(this.action.verifyTokenFail(response)))
				)
			)
		)
	);

	checkEmailAvailability$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailability),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap(([email, oidcToken]) =>
				this.service.checkEmailAvailability(email, oidcToken).pipe(
					map((response: EmailAvailability) => this.action.checkEmailAvailabilitySuccess(response)),
					catchError((response: AppError) => of(this.action.checkEmailAvailabilityFail(response)))
				)
			)
		)
	);

	unsubscribeFromNewsletter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletter),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.unsubscribeFromNewsletter(token).pipe(
					map(() => this.action.unsubscribeFromNewsletterSuccess()),
					catchError((response: AppError) => of(this.action.unsubscribeFromNewsletterFail(response)))
				)
			)
		)
	);

	acceptTermsAndConditions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditions),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.acceptTermsAndConditions(token).pipe(
					map(() => this.action.acceptTermsAndConditionsSuccess()),
					catchError((response: AppError) => of(this.action.acceptTermsAndConditionsFail(response)))
				)
			)
		)
	);

	acceptLatestTermsAndConditions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptLatestTermsAndConditions),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.acceptLatestTermsAndConditions(token).pipe(
					map(() => this.action.acceptTermsAndConditionsSuccess()),
					catchError((response: AppError) => of(this.action.acceptTermsAndConditionsFail(response)))
				)
			)
		)
	);

	declineTermsAndConditions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditions),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.declineTermsAndConditions(token).pipe(
					map(() => this.action.declineTermsAndConditionsSuccess()),
					catchError((response: AppError) => of(this.action.declineTermsAndConditionsFail(response)))
				)
			)
		)
	);

	declineTermsAndConditionsSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditionsSuccess),
			map(() => this.sessionAction.clearTermsAndConditionsToken())
		)
	);

	confirmProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfile),
			map(
				(action: ActionWithPayload<ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest>) =>
					action.payload
			),
			withLatestFrom(this.store.pipe(select(this.sessionSelector.getProfileNotCompletedData()))),
			switchMap(([requestData, profileNotCompletedData]) =>
				this.service.confirmProfile(requestData, profileNotCompletedData?.countryCode).pipe(
					map(() => this.action.ConfirmProfileSuccess()),
					catchError((error: AppError) => of(this.action.ConfirmProfileFail(error)))
				)
			)
		)
	);

	acceptDepositTermsAndConditions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptDepositTermsAndConditions),
			switchMap(() =>
				this.service.acceptDepositTermsAndConditions().pipe(
					map((response) => this.action.acceptDepositTermsAndConditionsSuccess(response)),
					catchError((error: AppError) => of(this.action.acceptDepositTermsAndConditionsFail(error)))
				)
			)
		)
	);

	acceptLatestPrivacyPolicy$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicy),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.acceptPrivacyPolicy(token).pipe(
					map(() => this.action.acceptPrivacyPolicySuccess()),
					catchError((response: AppError) => of(this.action.acceptPrivacyPolicyFail(response)))
				)
			)
		)
	);

	acceptEntityLegalNotice$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNotice),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((token) =>
				this.service.acceptEntityLegalNotice(token).pipe(
					map(() => this.action.acceptEntityLegalNoticeSuccess()),
					catchError((response: AppError) => of(this.action.acceptEntityLegalNoticeFail(response)))
				)
			)
		)
	);

	logoutSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map((x: ActionWithPayload<SessionEndReason>) => x.payload),
			map((reason) => this.action.clearCustomer(reason === SessionEndReason.SessionExpired))
		)
	);

	loginTermsAndCondition$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.login,
				SESSION_ACTION_TYPE.loginWithPersonalNumber,
				SESSION_ACTION_TYPE.loginWithVerificationCode
			),
			map(() => this.action.clearAcceptedLatestTermsAndConditions())
		)
	);

	loginPrivacyPolicy$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.login,
				SESSION_ACTION_TYPE.loginWithPersonalNumber,
				SESSION_ACTION_TYPE.loginWithVerificationCode
			),
			map(() => this.action.clearAcceptedLatestPrivacyPolicy())
		)
	);

	loginFailedTermsAndConditions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail, SESSION_ACTION_TYPE.loginWithTokenFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((error) => TERMS_AND_CONDITIONS_AUTH_CODES.includes(error.code)),
			map((error) => this.action.latestTermsAndConditionsNotAccepted(error))
		)
	);

	loginFailedPrivacyPolicy$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((error) => PRIVACY_POLICY_AUTH_CODES.includes(error.code)),
			map((error) => this.action.latestPrivacyPolicyNotAccepted(error))
		)
	);

	loginFailedEntityLegalNotice$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginFail),
			map((x: ActionWithPayload<AppError>) => x.payload),
			filter((error) => error.code === AUTH_MESSAGE_CODES.login.latestEntityLegalNoticeNotAccepted),
			map((error) => this.action.entityLegalNoticeNotAccepted(error))
		)
	);

	logoutSelfExcludedCustomer$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailSuccess),
				map((x: ActionWithPayload<Partial<Customer>>) => x.payload.allowedActions),
				ignoreNil(),
				tap((allowedActions) => {
					const isCustomerSelfExcluded = !allowedActions.login.isAllowed;

					if (isCustomerSelfExcluded) {
						this.store.dispatch(this.authAction.logout());
					}
				})
			),
		{ dispatch: false }
	);

	updateProfileValidation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CUSTOMER_MANAGEMENT_ACTION_TYPE.updateProfileValidation),
			switchMap(() => this.service.updateProfileValidation()),
			map(() => this.action.updateProfileValidationSuccess()),
			catchError((response: AppError) => of(this.action.updateProfileValidationFail(response)))
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerFail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailFail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerFail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerDetailFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyTokenFail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.verifyToken,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailabilityFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.checkEmailAvailability,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletterSuccess]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.newsletter,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditionsFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNoticeFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.entityLegalNotice,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditionsFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.confirmProfileFail]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.confirmProfile,
			}),
			this.messageService.mapErrorByContext$(
				{
					[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerFail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
					[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetailFail]:
						CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				},
				MessageScopeType.Global,
				MessageType.Fatal
			),
			this.messageService.mapClearByContext$({
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomer]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.getCustomerDetail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomer]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerDetail]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.verifyToken]: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.verifyToken,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.checkEmailAvailability]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.checkEmailAvailability,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.unsubscribeFromNewsletter]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.newsletter,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditions]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptLatestTermsAndConditions]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNotice]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.entityLegalNotice,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.declineTermsAndConditions]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicy]:
					CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.privacyPolicy,
			}),
			this.messageService.map$({
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerSuccess]: {
					context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateSuccess,
				},
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.updateCustomerFail]: {
					context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateFail,
				},
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptTermsAndConditionsSuccess]: {
					context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.termsAndConditions,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateSuccess,
				},
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptPrivacyPolicySuccess]: {
					context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.privacyPolicy,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateSuccess,
				},
				[CUSTOMER_MANAGEMENT_ACTION_TYPE.acceptEntityLegalNoticeSuccess]: {
					context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.entityLegalNotice,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateSuccess,
				},
			})
		)
	);

	sessionToSdk$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.store.select(CustomerManagementSelector.getCustomer).pipe(
					tap((customer) => {
						this.setCustomer(customer);
					})
				),
			{
				dispatch: false,
			}
		)
	);
	constructor(
		private actions$: Actions,
		private action: CustomerManagementAction,
		private service: CustomerManagementService,
		private authAction: AuthAction,
		private sessionAction: SessionAction,
		private messageService: MessageService,
		private platformService: PlatformService,
		private telemetryService: TelemetryService,
		private sessionSelector: SessionSelector,
		private store: Store<CoreState>,
		private documentRef: DocumentRef
	) {}

	private setCustomer(customer: any) {
		// TODO: update or move the session update from OBGA to SDK when fully integration will happen
		// initial implementation is done to support this ticket https://edgealpha.corpsson.com/browse/AG-8000
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.SetCustomer, { customer }); // do not remove `?` otherwise B2B breaks
	}
}
